/** Role-aware operator catalogs + parse helpers for the dashboard Navbar omnibar. */

export const OMNIBAR_STATUS_VALUES = Object.freeze([
  'pending',
  'accepted',
  'completed',
  'declined',
  'canceled',
]);

export const OMNIBAR_TYPE_VALUES_BRAND = Object.freeze([
  'application',
  'invite',
  'campaign',
]);

export const OMNIBAR_TYPE_VALUES_INFLUENCER = Object.freeze([
  'application',
  'invite',
]);

export const OMNIBAR_STATUS_DISPLAY = Object.freeze({
  pending: 'Pending',
  accepted: 'Accepted',
  completed: 'Completed',
  declined: 'Declined',
  canceled: 'Canceled',
});

export const OMNIBAR_TYPE_DISPLAY = Object.freeze({
  application: 'Application',
  invite: 'Invite',
  campaign: 'Campaign',
});

/** Operators that open a value/page picker instead of inserting a prefix immediately. */
export const OMNIBAR_DRILLDOWN_OPERATORS = Object.freeze(['status', 'type', 'go']);

const BRAND_OPERATORS = Object.freeze([
  {
    key: 'campaign',
    insertText: 'campaign:',
    friendlyLabel: 'Campaign',
    promptPlaceholder: 'Type a campaign name…',
    description: 'Search by campaign name',
  },
  {
    key: 'creator',
    insertText: 'creator:',
    friendlyLabel: 'Creator',
    promptPlaceholder: 'Type a creator name…',
    description: 'Search by creator name',
  },
  {
    key: 'status',
    insertText: 'status:',
    friendlyLabel: 'Status',
    promptPlaceholder: 'Choose a status…',
    description: 'Filter by collaboration status',
  },
  {
    key: 'type',
    insertText: 'type:',
    friendlyLabel: 'Type',
    promptPlaceholder: 'Choose a type…',
    description: 'Application, invite, or campaign',
  },
  {
    key: 'go',
    insertText: 'go:',
    friendlyLabel: 'Go to page',
    promptPlaceholder: 'Jump to a page…',
    description: 'Jump to a dashboard page',
  },
]);

const INFLUENCER_OPERATORS = Object.freeze([
  {
    key: 'brand',
    insertText: 'brand:',
    friendlyLabel: 'Brand',
    promptPlaceholder: 'Type a brand name…',
    description: 'Search by brand name',
  },
  {
    key: 'campaign',
    insertText: 'campaign:',
    friendlyLabel: 'Campaign',
    promptPlaceholder: 'Type a campaign name…',
    description: 'Search by campaign name',
  },
  {
    key: 'status',
    insertText: 'status:',
    friendlyLabel: 'Status',
    promptPlaceholder: 'Choose a status…',
    description: 'Filter by collaboration status',
  },
  {
    key: 'type',
    insertText: 'type:',
    friendlyLabel: 'Type',
    promptPlaceholder: 'Choose a type…',
    description: 'Application or invite',
  },
  {
    key: 'go',
    insertText: 'go:',
    friendlyLabel: 'Go to page',
    promptPlaceholder: 'Jump to a page…',
    description: 'Jump to a dashboard page',
  },
]);

const ADMIN_OPERATORS = Object.freeze([
  {
    key: 'go',
    insertText: 'go:',
    friendlyLabel: 'Go to page',
    promptPlaceholder: 'Jump to a page…',
    description: 'Jump to an admin page',
  },
]);

export const getOmnibarOperators = (userType) => {
  if (userType === 'influencer') return INFLUENCER_OPERATORS;
  if (userType === 'admin') return ADMIN_OPERATORS;
  return BRAND_OPERATORS;
};

export const getOmnibarOperatorKeys = (userType) => (
  getOmnibarOperators(userType).map((op) => op.key)
);

export const getOmnibarOperator = (userType, key) => (
  getOmnibarOperators(userType).find((op) => op.key === key) || null
);

export const getOmnibarTypeValues = (userType) => (
  userType === 'influencer' ? OMNIBAR_TYPE_VALUES_INFLUENCER : OMNIBAR_TYPE_VALUES_BRAND
);

export const isOmnibarDrilldownOperator = (key) => (
  OMNIBAR_DRILLDOWN_OPERATORS.includes(key)
);

export const formatOmnibarStatusLabel = (value) => (
  OMNIBAR_STATUS_DISPLAY[String(value || '').toLowerCase()]
  || String(value || '')
);

export const formatOmnibarTypeLabel = (value) => (
  OMNIBAR_TYPE_DISPLAY[String(value || '').toLowerCase()]
  || String(value || '')
);

/**
 * Friendly chip label for an active operator draft (e.g. "Status: Pending", "Campaign").
 */
export const formatOmnibarActiveFilterChip = (parsed, userType) => {
  if (!parsed?.operator) return null;
  const op = getOmnibarOperator(userType, parsed.operator);
  if (!op) return null;

  const value = String(parsed.value || '').trim();
  if (!value) {
    return op.friendlyLabel;
  }

  if (parsed.operator === 'status') {
    return `${op.friendlyLabel}: ${formatOmnibarStatusLabel(value)}`;
  }
  if (parsed.operator === 'type') {
    return `${op.friendlyLabel}: ${formatOmnibarTypeLabel(value)}`;
  }
  if (parsed.operator === 'go') {
    return `${op.friendlyLabel}: ${value}`;
  }

  return op.friendlyLabel;
};

/**
 * Split a draft into operator key + raw value segment (preserves user typing).
 * Returns null when the draft is not a known operator prefix.
 */
export const splitOmnibarOperatorDraft = (query, userType) => {
  const raw = String(query || '');
  const colonIndex = raw.indexOf(':');
  if (colonIndex <= 0) return null;

  const key = raw.slice(0, colonIndex).toLowerCase();
  const known = getOmnibarOperatorKeys(userType);
  if (!known.includes(key)) return null;

  return {
    operator: key,
    valuePart: raw.slice(colonIndex + 1),
  };
};

/**
 * Parse `key:value` queries. Unknown keys fall through as free text.
 */
export const parseOmnibarQuery = (query, userType) => {
  const trimmed = String(query || '').trim();
  if (!trimmed) {
    return { operator: null, value: '', freeText: '', raw: '' };
  }

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex <= 0) {
    return { operator: null, value: '', freeText: trimmed, raw: trimmed };
  }

  const key = trimmed.slice(0, colonIndex).toLowerCase();
  const known = getOmnibarOperatorKeys(userType);
  if (!known.includes(key)) {
    return { operator: null, value: '', freeText: trimmed, raw: trimmed };
  }

  return {
    operator: key,
    value: trimmed.slice(colonIndex + 1).trim(),
    freeText: '',
    raw: trimmed,
  };
};

export const includesIgnoreCase = (haystack, needle) => {
  if (!needle) return true;
  return String(haystack || '').toLowerCase().includes(String(needle).toLowerCase());
};

export const startsWithIgnoreCase = (haystack, needle) => {
  if (!needle) return true;
  return String(haystack || '').toLowerCase().startsWith(String(needle).toLowerCase());
};
